import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Code2, ExternalLink } from 'lucide-react'
import { Badge, formatDateTime } from '@/kit/ui'
import { agentStartHint, getAgent } from '@/lib/agent'
import { apiMode } from '@/lib/hubApi'
import { compareVersions, ofCode } from '@/lib/batch'
import { KNOWN_OUTCOMES, MAX_TIMEOUT_MS } from '@/lib/outputCheck'
import { useCodeStore } from '@/store/codeStore'
import { useSettingsStore } from '@/store/settingsStore'
import type { Lender } from '@/types'
import { BatchStatusBadge } from './BatchBits'
import { playgroundUrl } from '../links'
import { ProductPicker, useBatchProduct } from './ProductPicker'

/**
 * 3.3 Playground in the lender tab: the playground itself opens on its own page (a VS Code window on a real Node
 * container, LH-F5); here are the versions to open it on, whether the agent is running, and the rules the system checks.
 */
export function PlaygroundSubTab({ lender }: { lender: Lender }) {
  const batches = useCodeStore((s) => s.batches)
  const settings = useSettingsStore((s) => s.settings)
  const { choices, chosen, productSlug, choose } = useBatchProduct(lender)
  const mine = useMemo(() => ofCode(batches, lender.slug, productSlug).sort((a, b) => compareVersions(b.version, a.version)), [batches, lender.slug, productSlug])
  const main = mine.find((b) => b.status === 'draft' || b.status === 'tests_passed') ?? mine.find((b) => b.status === 'live') ?? mine[0]
  const [agent, setAgent] = useState<{ ok: boolean; runtime?: string; note?: string | null } | null>(null)
  useEffect(() => {
    let stop = false
    getAgent().health().then((h) => !stop && setAgent(h)).catch(() => !stop && setAgent({ ok: false }))
    return () => { stop = true }
  }, [])

  return (
    <>
      <ProductPicker choices={choices} chosen={chosen} onChoose={choose} />
      <div className="card mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-white"><Code2 size={20} /></span>
          <div className="min-w-0">
            <h2 className="section-title !mb-0">The playground opens on its own page, on a real Node container</h2>
            <p className="text-xs text-muted">Explorer with folders · editor tabs · Problems, Output, Terminal (a real shell: npm install, node, git), Console · Run with Ctrl+Enter · Save = one commit.</p>
            <p className="mt-1 text-xs" data-testid="agent-status">
              {agent === null ? <span className="text-muted">Checking the playground…</span> : agent.ok ? <><Badge tone="success">{apiMode() ? 'playground on the Hub' : 'agent running'}</Badge> <span className="text-muted">{agent.runtime === 'docker' ? 'Docker: one hub-sandbox container per batch' : `local runtime on this PC${agent.note ? ` (${agent.note})` : ''}`}</span></> : <><Badge tone="danger">{apiMode() ? 'playground offline' : 'agent offline'}</Badge> <span className="text-muted">The page opens read-only. {agentStartHint()}{agent.note ? ` (${agent.note})` : ''}</span></>}
            </p>
          </div>
        </div>
        {main && <Link to={playgroundUrl(lender.slug, main.version, undefined, productSlug)} className="btn btn-primary" data-testid="open-playground"><ExternalLink size={13} /> Open playground · {main.version}</Link>}
      </div>

      <div className="card-flush mb-3 overflow-x-auto">
        <table className="table">
          <thead><tr><th>Version</th><th>Status</th><th>Endpoints</th><th>Tests</th><th>Last commit</th><th /></tr></thead>
          <tbody>
            {mine.map((b) => (
              <tr key={b.id}>
                <td className="mono"><b>{b.version}</b></td>
                <td><BatchStatusBadge status={b.status} /></td>
                <td className="mono text-2xs">{b.manifest.endpoints.map((e) => e.path).join(' · ') || '—'}</td>
                <td>{b.testRun ? `${b.testRun.passed} / ${b.testRun.total}` : `${b.tests.length} not run`}</td>
                <td className="text-2xs">{b.commits[0] ? <><code>{b.commits[0].sha}</code> {b.commits[0].message} · {formatDateTime(b.commits[0].at)}</> : '—'}</td>
                <td className="text-right"><Link to={playgroundUrl(lender.slug, b.version, undefined, productSlug)} className="btn btn-secondary btn-sm">{b.status === 'draft' || b.status === 'tests_passed' ? 'Open' : 'View'}</Link></td>
              </tr>
            ))}
            {!mine.length && <tr><td colSpan={6} className="text-xs text-muted">No batch yet: Create API (3.1) makes the first one.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <section className="card">
          <h2 className="section-title">A batch is a Node project</h2>
          <p className="text-xs text-muted">Laid out like any Node service: <code>src/server.js</code> starts it (<code>npm start</code>), <code>src/app.js</code> mounts every endpoint, <code>src/api/v2/get-offer.js</code> is <code>POST /v2/get-offer</code> (one REST endpoint per file, folder = route), the lender client and helpers live in <code>src/services/</code>. An endpoint file is <code>module.exports = defineApi({'{'} outcomes, handler(applicant, ctx) {'}'})</code> and calls the lender with <code>ctx.lender(call)</code>; <code>npm install</code> what you need. <b>manifest.json</b> (endpoints, outcomes, variables) is generated by Discover and by Build, never written by hand.</p>
        </section>
        <section className="card">
          <h2 className="section-title">What the system checks (or: not approved)</h2>
          <p className="text-xs text-muted">The handler returns <code>{'{'} outcome, reply_code {'}'}</code>; outcome is one of {KNOWN_OUTCOMES.join(', ')} and one the endpoint declares; <i>offer</i> needs offers (amount, tenureMonths, ratePa); <i>redirect</i> needs an https link. The call to <code>ctx.lender()</code> has a known method, an https url without “undefined”, text headers, timeoutMs up to {MAX_TIMEOUT_MS / 1000} s. A test is green only with the right outcome <b>and</b> a clean check; one red test keeps the batch a draft.</p>
        </section>
        <section className="card">
          <h2 className="section-title">Rules</h2>
          <p className="text-xs text-muted">Save = one git commit in your name, no autosave · editors edit and test, admins build and make live · built or live = read-only (New version from this) · variables come from tab 5 (<code>.env</code> in the container, never in git) · UAT by default; production calls only by an admin, with a red banner and a History row · containers: {settings.runner.memoryMb} MB each, {settings.runner.deadlineMs / 1000} s per lender call (Settings).</p>
        </section>
      </div>
    </>
  )
}
